import { useDispatch, useSelector } from 'react-redux';
import ProductUnitService from '../../services/ProductUnitService';
import SupplierService from '../../services/supplier/SupplierService';
import RestaurantPurchaseOrderService from '../../services/restaurant/RestaurantPurchaseOrderService';
import RestaurantRawMaterialService from '../../services/restaurant/RestaurantRawMaterialService';
import RestaurantRawMaterialCategoryService from '../../services/restaurant/RestaurantRawMaterialCategoryService';
import RestaurantUnitConversionService from '../../services/restaurant/RestaurantUnitConversionService';
import PaymentConditionService from '../../services/PaymentConditionService';
import TermService from '../../services/term/TermService';
import { AppDispatch, RootState } from '../../store/store';
import {
  addPurchaseOrderDetail,
  appendPurchaseOrderCategory,
  appendPurchaseOrderRawMaterial,
  setPurchaseOrderSuppliers,
  setPurchaseOrderConversions,
  closePurchaseOrderDetail,
  closePurchaseOrderForm,
  openPurchaseOrderCreate,
  openPurchaseOrderDetail,
  openPurchaseOrderEdit,
  removePurchaseOrderDetail,
  setPurchaseOrderActing,
  setPurchaseOrderLoading,
  setPurchaseOrderOptions,
  setPurchaseOrderOptionsLoading,
  setPurchaseOrders,
  setPurchaseOrderSaving,
  updatePurchaseOrderDetail,
  updatePurchaseOrderField,
} from '../../store/slices/restaurant/restaurantPurchaseOrderSlice';
import {
  RestaurantPurchaseOrderDetailForm,
  RestaurantPurchaseOrderDTO,
  RestaurantPurchaseOrderFilters,
  RestaurantPurchaseOrderForm,
} from '../../types/restaurant/RestaurantPurchaseOrderTypes';
import {
  RestaurantRawMaterialDTO,
  RestaurantRawMaterialForm,
} from '../../types/restaurant/RestaurantRawMaterialTypes';
import { RestaurantRawMaterialCategoryDTO } from '../../types/restaurant/RestaurantRawMaterialCategoryTypes';
import { RESTAURANT_INVENTORY_UPDATED_EVENT } from '../../features/restaurant/production/restaurantInventoryEvents';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantPurchaseOrders = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector((root: RootState) => root.restaurantPurchaseOrder);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirmDialog = useConfirm();

  const loadOptions = async () => {
    if (!branchId) return;
    dispatch(setPurchaseOrderOptionsLoading(true));
    try {
      const [
        supplierResponse,
        rawMaterialResponse,
        categoryResponse,
        unitResponse,
        conversionResponse,
        paymentConditionResponse,
        termResponse,
      ] = await Promise.all([
        SupplierService.getDropdown(),
        RestaurantRawMaterialService.listFiltered(branchId, {
          page: 0,
          size: 50,
          sort: ['name,asc', 'id,asc'],
          filters: {
            search: null,
            categoryId: null,
            unitId: null,
            supplierId: null,
            active: true,
          },
        }),
        RestaurantRawMaterialCategoryService.list(branchId, true),
        // getAll (no getDropdown): necesitamos unitCode + mhCode para mapear la
        // unidad del DTE (uniMedida) a nuestra tabla y para mostrar el código.
        ProductUnitService.getAll(),
        RestaurantUnitConversionService.list({ activeOnly: true }),
        PaymentConditionService.fetchDropdown(),
        TermService.fetchDropdown(),
      ]);
      dispatch(
        setPurchaseOrderOptions({
          suppliers: supplierResponse?.data ?? [],
          rawMaterials: rawMaterialResponse.content,
          categories: categoryResponse.data ?? [],
          units: (unitResponse?.data ?? unitResponse ?? []).filter(
            (unit: { status?: boolean }) => unit.status !== false
          ),
          conversions: conversionResponse.data ?? [],
          paymentConditions:
            paymentConditionResponse.data ?? paymentConditionResponse ?? [],
          terms: termResponse.data ?? termResponse ?? [],
        })
      );
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setPurchaseOrderOptionsLoading(false));
    }
  };

  const load = async (filters: RestaurantPurchaseOrderFilters = {}) => {
    if (!branchId) return;
    dispatch(setPurchaseOrderLoading(true));
    try {
      const response = await RestaurantPurchaseOrderService.list(
        branchId,
        filters
      );
      dispatch(setPurchaseOrders(response));
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setPurchaseOrderLoading(false));
    }
  };

  const view = async (item: Pick<RestaurantPurchaseOrderDTO, 'id'>) => {
    try {
      const response = await RestaurantPurchaseOrderService.get(item.id);
      dispatch(openPurchaseOrderDetail(response.data));
    } catch (error) {
      handle(error);
    }
  };

  const edit = async (item: RestaurantPurchaseOrderDTO) => {
    if (item.status !== 'DRAFT') return;
    try {
      const response = await RestaurantPurchaseOrderService.get(item.id);
      const purchase = response.data;
      dispatch(
        openPurchaseOrderEdit({
          id: purchase.id,
          supplierId: purchase.supplierId,
          purchaseDate: purchase.purchaseDate,
          paymentCondition: purchase.paymentCondition ?? null,
          notes: purchase.notes ?? '',
          // Preserva el desglose fiscal del DTE al re-guardar un borrador importado.
          dte: purchase.dteControlNumber
            ? {
                type: purchase.dteType ?? '',
                controlNumber: purchase.dteControlNumber ?? '',
                generationCode: purchase.dteGenerationCode ?? '',
                taxedTotal: purchase.dteTaxedTotal,
                exemptTotal: purchase.dteExemptTotal,
                nonSubjectTotal: purchase.dteNonSubjectTotal,
                discountTotal: purchase.dteDiscountTotal,
                ivaTotal: purchase.dteIvaTotal,
                ivaPerception: purchase.dteIvaPerception,
                ivaRetention: purchase.dteIvaRetention,
                incomeRetention: purchase.dteIncomeRetention,
                operationTotal: purchase.dteOperationTotal,
                payableTotal: purchase.dtePayableTotal,
              }
            : undefined,
          details: (purchase.details ?? []).map((detail) => ({
            key: detail.id,
            rawMaterialId: detail.rawMaterialId,
            quantity: detail.quantity,
            unitId: detail.unitId,
            unitCost: detail.unitCost,
            discountAmount: detail.discountAmount,
            taxAmount: detail.taxAmount,
            dteTaxedSale: detail.dteTaxedSale,
            dteExemptSale: detail.dteExemptSale,
            dteNonSubjectSale: detail.dteNonSubjectSale,
            dteDiscountAmount: detail.dteDiscountAmount,
            expirationDate: detail.expirationDate ?? '',
            lotNumber: detail.lotNumber ?? '',
          })),
        })
      );
    } catch (error) {
      handle(error);
    }
  };

  const save = async (filters: RestaurantPurchaseOrderFilters = {}) => {
    if (!branchId || state.saving) return;
    dispatch(setPurchaseOrderSaving(true));
    try {
      const response = state.form.id
        ? await RestaurantPurchaseOrderService.update(state.form.id, state.form)
        : await RestaurantPurchaseOrderService.create(branchId, state.form);
      show(
        response.type || 'success',
        response.title || 'Compra guardada',
        response.message || 'La compra quedó guardada como borrador.'
      );
      dispatch(closePurchaseOrderForm());
      await load(filters);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setPurchaseOrderSaving(false));
    }
  };

  const runStatusAction = async (
    item: RestaurantPurchaseOrderDTO,
    action: 'confirm' | 'cancel',
    filters: RestaurantPurchaseOrderFilters
  ) => {
    dispatch(setPurchaseOrderActing(item.id));
    try {
      const response =
        action === 'confirm'
          ? await RestaurantPurchaseOrderService.confirm(item.id)
          : await RestaurantPurchaseOrderService.cancel(item.id);
      show(
        response.type || 'success',
        response.title ||
          (action === 'confirm' ? 'Compra confirmada' : 'Compra cancelada'),
        response.message ||
          (action === 'confirm'
            ? 'El inventario fue actualizado correctamente.'
            : 'La compra fue cancelada.')
      );
      if (action === 'confirm')
        window.dispatchEvent(new Event(RESTAURANT_INVENTORY_UPDATED_EVENT));
      dispatch(closePurchaseOrderDetail());
      await load(filters);
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setPurchaseOrderActing(null));
    }
  };

  const requestStatusAction = (
    item: RestaurantPurchaseOrderDTO,
    action: 'confirm' | 'cancel',
    filters: RestaurantPurchaseOrderFilters = {}
  ) =>
    confirmDialog({
      title: action === 'confirm' ? 'Confirmar compra' : 'Cancelar compra',
      message:
        action === 'confirm'
          ? `¿Confirmar ${item.purchaseNumber}? Esta acción ingresará la materia prima al inventario y no podrá deshacerse.`
          : `¿Cancelar ${item.purchaseNumber}? La compra dejará de ser editable.`,
      icon: 'warning',
      confirmText: action === 'confirm' ? 'Sí, confirmar' : 'Sí, cancelar',
      cancelText: 'Volver',
      onConfirm: () => runStatusAction(item, action, filters),
    });

  const updateField = <
    K extends keyof Omit<RestaurantPurchaseOrderForm, 'id' | 'details'>
  >(
    field: K,
    value: RestaurantPurchaseOrderForm[K]
  ) => dispatch(updatePurchaseOrderField({ field, value }));

  const updateDetail = <
    K extends keyof Omit<RestaurantPurchaseOrderDetailForm, 'key'>
  >(
    key: number,
    field: K,
    value: RestaurantPurchaseOrderDetailForm[K]
  ) => dispatch(updatePurchaseOrderDetail({ key, field, value }));

  // =====================================================
  // 🔹 Alta al vuelo desde el modal de compra
  // =====================================================

  const createRawMaterialInline = async (input: {
    name: string;
    categoryId: number;
    unitId: number;
  }): Promise<RestaurantRawMaterialDTO | null> => {
    if (!branchId) return null;
    try {
      const form: RestaurantRawMaterialForm = {
        categoryId: input.categoryId,
        unitId: input.unitId,
        supplierId: '',
        code: '', // el backend lo genera desde la categoría
        name: input.name,
        description: '',
        minimumStock: '',
        currentStock: '',
        cost: '',
        barcode: '',
      };
      const response = await RestaurantRawMaterialService.create(branchId, form);
      const dto = response.data;
      dispatch(appendPurchaseOrderRawMaterial(dto));
      show(
        response.type || 'success',
        response.title || 'Materia prima creada',
        response.message || `${dto.name} (${dto.code}) quedó lista para usar.`
      );
      return dto;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  const reloadSuppliers = async () => {
    try {
      const response = await SupplierService.getDropdown();
      dispatch(setPurchaseOrderSuppliers(response?.data ?? []));
    } catch (error) {
      handle(error);
    }
  };

  // Refresca las reglas de conversión (p. ej. tras crear una equivalencia de
  // empaque desde el asistente de importación de DTE).
  const reloadConversions = async () => {
    try {
      const response = await RestaurantUnitConversionService.list({
        activeOnly: true,
      });
      dispatch(setPurchaseOrderConversions(response.data ?? []));
    } catch (error) {
      handle(error);
    }
  };

  const createCategoryInline = async (
    name: string
  ): Promise<RestaurantRawMaterialCategoryDTO | null> => {
    if (!branchId) return null;
    try {
      const response = await RestaurantRawMaterialCategoryService.create(
        branchId,
        { name, description: '', icon: '', color: '', displayOrder: 1 }
      );
      dispatch(appendPurchaseOrderCategory(response.data));
      return response.data;
    } catch (error) {
      handle(error);
      return null;
    }
  };

  return {
    ...state,
    load,
    loadOptions,
    view,
    edit,
    save,
    updateField,
    updateDetail,
    createRawMaterialInline,
    createCategoryInline,
    reloadSuppliers,
    reloadConversions,
    confirm: (
      item: RestaurantPurchaseOrderDTO,
      filters?: RestaurantPurchaseOrderFilters
    ) => requestStatusAction(item, 'confirm', filters),
    cancel: (
      item: RestaurantPurchaseOrderDTO,
      filters?: RestaurantPurchaseOrderFilters
    ) => requestStatusAction(item, 'cancel', filters),
    openCreate: () => dispatch(openPurchaseOrderCreate()),
    // Abre el formulario de compra ya rellenado (p. ej. desde la importación de
    // un DTE). Sin `id` → al guardar se crea una compra nueva.
    openImported: (form: RestaurantPurchaseOrderForm) =>
      dispatch(openPurchaseOrderEdit(form)),
    closeForm: () => dispatch(closePurchaseOrderForm()),
    closeDetail: () => dispatch(closePurchaseOrderDetail()),
    addDetail: () => dispatch(addPurchaseOrderDetail()),
    removeDetail: (key: number) => dispatch(removePurchaseOrderDetail(key)),
  };
};

export default useRestaurantPurchaseOrders;
